import React, { useState } from 'react';
import { Key, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';

interface EncryptionKey {
  id: string;
  userId: string;
  userEmail: string;
  algorithm: string;
  status: 'active' | 'rotated' | 'compromised';
  createdAt: string;
  lastRotated?: string;
  rotationDue: boolean;
}

export const EncryptionManager: React.FC = () => {
  const [encryptionKeys] = useState<EncryptionKey[]>([
    {
      id: 'key-admin-1',
      userId: 'admin-1',
      userEmail: 'admin@healthtracker.com',
      algorithm: 'AES-256-GCM',
      status: 'active',
      createdAt: '2024-01-01T00:00:00Z',
      lastRotated: '2024-01-01T00:00:00Z',
      rotationDue: false,
    },
    {
      id: 'key-patient-1',
      userId: 'patient-1',
      userEmail: 'john.doe@example.com',
      algorithm: 'AES-256-GCM',
      status: 'active',
      createdAt: '2024-01-01T00:00:00Z',
      lastRotated: '2023-10-01T00:00:00Z',
      rotationDue: true,
    },
    {
      id: 'key-provider-1',
      userId: 'provider-1',
      userEmail: 'dr.smith@medicalcenter.com',
      algorithm: 'AES-256-GCM',
      status: 'active',
      createdAt: '2024-01-01T00:00:00Z',
      lastRotated: '2024-01-01T00:00:00Z',
      rotationDue: false,
    },
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'rotated': return 'bg-blue-100 text-blue-800';
      case 'compromised': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'rotated': return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'compromised': return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default: return <Key className="h-4 w-4 text-gray-500" />;
    }
  };

  const keyStats = {
    total: encryptionKeys.length,
    active: encryptionKeys.filter(k => k.status === 'active').length,
    rotationDue: encryptionKeys.filter(k => k.rotationDue).length,
    compromised: encryptionKeys.filter(k => k.status === 'compromised').length,
  };

  return (
    <div className="space-y-6">
      {/* Encryption Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Total Keys</p>
              <p className="text-2xl font-bold text-slate-800">{keyStats.total}</p>
            </div>
            <Key className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Active Keys</p>
              <p className="text-2xl font-bold text-slate-800">{keyStats.active}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Rotation Due</p>
              <p className="text-2xl font-bold text-slate-800">{keyStats.rotationDue}</p>
            </div>
            <RefreshCw className="h-8 w-8 text-orange-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 border border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Compromised</p>
              <p className="text-2xl font-bold text-slate-800">{keyStats.compromised}</p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Encryption Management */}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-slate-800">Encryption Key Management</h3>
              <p className="text-sm text-slate-600">Manage user-specific encryption keys for data protection</p>
            </div>
            <button className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors">
              <RefreshCw className="h-4 w-4" />
              <span>Rotate All Due Keys</span>
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Key ID</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Algorithm</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Last Rotated</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {encryptionKeys.map((key) => (
                  <tr key={key.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-slate-800">{key.userEmail}</p>
                        <p className="text-sm text-slate-600">ID: {key.userId}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                        {key.id}
                      </code>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-slate-600">{key.algorithm}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(key.status)}
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(key.status)}`}>
                          {key.status}
                        </span>
                        {key.rotationDue && (
                          <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded-full">
                            Rotation Due
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className="text-sm text-slate-600">
                        {key.lastRotated ? new Date(key.lastRotated).toLocaleDateString() : 'Never'}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <button className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Rotate Key">
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button className="p-1 text-orange-600 hover:bg-orange-50 rounded transition-colors" title="Mark Compromised">
                          <AlertTriangle className="h-4 w-4" />
                        </button>
                        <button className="p-1 text-slate-600 hover:bg-slate-100 rounded transition-colors" title="View Details">
                          <Key className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Key Rotation Policy */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Key Rotation Policy</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Automatic Rotation Period
              </label>
              <select className="w-full p-3 border border-slate-300 rounded-lg">
                <option value="90">90 days</option>
                <option value="180">180 days</option>
                <option value="365">1 year</option>
                <option value="never">Manual only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Key Strength
              </label>
              <select className="w-full p-3 border border-slate-300 rounded-lg">
                <option value="AES-256-GCM">AES-256-GCM</option>
                <option value="ChaCha20-Poly1305">ChaCha20-Poly1305</option>
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <input type="checkbox" id="auto-rotate" className="w-4 h-4 text-blue-600" defaultChecked />
              <label htmlFor="auto-rotate" className="text-sm text-slate-700">
                Enable automatic key rotation
              </label>
            </div>
            <div className="flex items-center space-x-3">
              <input type="checkbox" id="notify-rotation" className="w-4 h-4 text-blue-600" defaultChecked />
              <label htmlFor="notify-rotation" className="text-sm text-slate-700">
                Notify users before key rotation
              </label>
            </div>
            <div className="flex items-center space-x-3">
              <input type="checkbox" id="emergency-rotation" className="w-4 h-4 text-blue-600" />
              <label htmlFor="emergency-rotation" className="text-sm text-slate-700">
                Enable emergency rotation protocol
              </label>
            </div>
          </div>
        </div>
        <button className="mt-6 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors">
          Save Policy Settings
        </button>
      </div>
    </div>
  );
};
